import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { defaultEditorTheme } from '../templateEditor.theme'
import { calcFitZoom, clampZoom } from '../templateEditor.utils'

const theme = defaultEditorTheme

type ViewportRef = RefObject<HTMLDivElement>

const getViewportContentSize = (viewport: HTMLDivElement) => {
    const style = getComputedStyle(viewport)
    const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
    const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)

    return {
        width: Math.max(viewport.clientWidth - padX, 1),
        height: Math.max(viewport.clientHeight - padY, 1),
    }
}

export const useTemplateEditorZoom = (viewportRef: ViewportRef, canvasW: number, canvasH: number) => {
    const [zoom, setZoom] = useState(1)
    const [isFitMode, setIsFitMode] = useState(false)
    const isFitModeRef = useRef(false)
    isFitModeRef.current = isFitMode

    const applyZoom = useCallback(
        (nextZoom: number, focal?: { clientX: number; clientY: number }) => {
            const viewport = viewportRef.current
            const host = viewport?.querySelector('[data-template-editor-zoom-host]') as HTMLElement | null
            const clamped = clampZoom(nextZoom, theme)
            const prevZoom = zoom || 1

            if (Math.abs(clamped - prevZoom) < 0.001) {
                setIsFitMode(false)
                return
            }

            if (viewport && host && focal) {
                const hostRect = host.getBoundingClientRect()
                const ratio = clamped / prevZoom
                const offsetX = focal.clientX - hostRect.left
                const offsetY = focal.clientY - hostRect.top
                viewport.scrollLeft += offsetX * (ratio - 1)
                viewport.scrollTop += offsetY * (ratio - 1)
            }

            setIsFitMode(false)
            setZoom(clamped)
        },
        [viewportRef, zoom]
    )

    const fitToWindow = useCallback(() => {
        const viewport = viewportRef.current
        const displayW = canvasW * theme.displayScale
        const displayH = canvasH * theme.displayScale

        if (!viewport || displayW <= 0 || displayH <= 0) {
            setZoom(1)
            setIsFitMode(true)
            return
        }

        const { width, height } = getViewportContentSize(viewport)
        const next = calcFitZoom(width, height, displayW, displayH, { ...theme, fitPadding: 0 })

        setZoom(next)
        setIsFitMode(true)

        requestAnimationFrame(() => {
            const vp = viewportRef.current
            if (!vp) {
                return
            }
            vp.scrollLeft = Math.max(0, (vp.scrollWidth - vp.clientWidth) / 2)
            vp.scrollTop = Math.max(0, (vp.scrollHeight - vp.clientHeight) / 2)
        })
    }, [canvasH, canvasW, viewportRef])

    /** Обрабатывает селект зума в тулбаре: fit, пресет или custom. */
    const handleZoomSelect = useCallback(
        (value: string) => {
            if (value === 'fit') {
                fitToWindow()
                return
            }
            if (value === 'custom') {
                return
            }
            applyZoom(Number(value))
        },
        [applyZoom, fitToWindow]
    )

    useEffect(() => {
        if (!isFitMode) {
            return
        }
        fitToWindow()
    }, [canvasW, canvasH, fitToWindow, isFitMode])

    useEffect(() => {
        const onResize = () => {
            if (isFitModeRef.current) {
                fitToWindow()
            }
        }

        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [fitToWindow])

    useEffect(() => {
        const viewport = viewportRef.current
        if (!viewport) {
            return
        }

        const onWheel = (event: WheelEvent) => {
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault()
                const factor = Math.exp(-event.deltaY * 0.0045)
                applyZoom(zoom * factor, { clientX: event.clientX, clientY: event.clientY })
                return
            }

            const deltaScale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientHeight : 1
            const dx = event.deltaX * deltaScale
            const dy = event.deltaY * deltaScale
            if (!dx && !dy) {
                return
            }

            event.preventDefault()
            viewport.scrollLeft += dx
            viewport.scrollTop += dy
        }

        viewport.addEventListener('wheel', onWheel, { passive: false })
        return () => viewport.removeEventListener('wheel', onWheel)
    }, [applyZoom, viewportRef, zoom])

    return {
        zoom,
        isFitMode,
        applyZoom,
        fitToWindow,
        handleZoomSelect,
    }
}
